import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Button } from 'primeng/button';
import { BehaviorSubject } from 'rxjs';

export interface WebcamImageData {
  fileName: string;
  size: number;
  mimeType: string;
  base64Data: string; // Imagen original de alta calidad
  previewData?: string; // Imagen optimizada para preview (opcional)
  file?: string;
  url?: string;
  errors?: string[];
  timestamp: Date;
}

@Component({
  selector: 'webcam-capture-form',
  templateUrl: './webcam-capture-form.component.html',
  styleUrls: ['./webcam-capture-form.component.scss'],
  standalone: true,
  imports: [CommonModule, Button],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WebcamCaptureFormComponent),
      multi: true
    }
  ]
})
export class WebcamCaptureFormComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() title: string = '';
  @Input() description: string = '';
  @Input() width: number = 640;
  @Input() height: number = 480;
  @Input() quality: number = 0.8;
  @Input() showPreview: boolean = true;
  @Input() showCaptureButton: boolean = true;
  @Input() captureButtonMode: 'floating' | 'external' = 'floating'; // 'floating' o 'external'
  @Input() showRetakeButton: boolean = true;
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() margin: string = '';
  @Input() showError: boolean = true;
  @Input() messageError: string = '';
  @Input() autoStartCamera: boolean = true;

  @Output() cameraError = new EventEmitter<string>();
  @Output() photoCaptured = new EventEmitter<WebcamImageData>();
  @Output() photoDeleted = new EventEmitter<void>();

  // Observable para notificar cambios en el valor
  public valueChange = new BehaviorSubject<WebcamImageData | null>(null);

  value: WebcamImageData | null = null;
  errorMessage: string = '';
  showWebcam = true;
  allowCameraSwitch = true;
  multipleWebcamsAvailable = false;

  // Implementación nativa del navegador
  @ViewChild('videoElement', { static: false }) videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement', { static: false }) canvasElement!: ElementRef<HTMLCanvasElement>;

  private stream: MediaStream | null = null;
  private isInitialized = false;

  // Full size image modal
  public showFullSizeModal = false;

  private onChange = (value: WebcamImageData | null) => {
    this.valueChange.next(value);
  };
  private onTouched = () => {};

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  ngOnInit() {
    this.showWebcam = this.autoStartCamera;

    // Esperar permisos antes de detectar cámaras
    this.waitForCameraPermissions();
  }

  // Método para esperar permisos de cámara
  private async waitForCameraPermissions(): Promise<void> {
    try {
      // Verificar si ya tenemos permisos
      const permissions = await navigator.permissions.query({ name: 'camera' as PermissionName });

      if (permissions.state === 'granted') {
        await this.checkMultipleWebcams();
        // Inicializar cámara después de detectar
        if (this.autoStartCamera) {
          await this.initializeCameraAfterPermissions();
        }
      } else if (permissions.state === 'prompt') {
        // Intentar obtener permisos solicitando acceso
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          stream.getTracks().forEach(track => track.stop());
          await this.checkMultipleWebcams();
          // Inicializar cámara después de detectar
          if (this.autoStartCamera) {
            await this.initializeCameraAfterPermissions();
          }
        } catch (error) {
          this.multipleWebcamsAvailable = false;
        }
      } else if (permissions.state === 'denied') {
        this.multipleWebcamsAvailable = false;
      }

      // Escuchar cambios en permisos
      permissions.onchange = async () => {
        if (permissions.state === 'granted') {
          await this.checkMultipleWebcams();
          // Si autoStartCamera está activo, reinicializar
          if (this.autoStartCamera && !this.isInitialized) {
            await this.initializeCameraAfterPermissions();
          }
        }
      };

    } catch (error) {
      // Fallback: intentar detectar cámaras directamente
      await this.checkMultipleWebcams();
      // Intentar inicializar cámara
      if (this.autoStartCamera) {
        await this.initializeCameraAfterPermissions();
      }
    }
  }

  // Método para inicializar cámara después de tener permisos
  private async initializeCameraAfterPermissions(): Promise<void> {
    // Esperar a que Angular esté completamente listo
    await Promise.resolve();
    await Promise.resolve();

    // Ahora inicializar la cámara
    await this.startCamera();
  }

  ngOnDestroy() {
    this.stopCamera();
    this.valueChange.complete();
  }

  // Propiedad para almacenar dispositivos disponibles
  private availableDevices: MediaDeviceInfo[] = [];
  private currentDeviceIndex = 0;

  // Nuevas propiedades para agrupación inteligente
  public frontCameras: MediaDeviceInfo[] = [];
  public backCameras: MediaDeviceInfo[] = [];
  public currentCameraGroup: 'front' | 'back' = 'back'; // Por defecto cámara trasera
  public currentGroupIndex = 0; // Índice dentro del grupo actual
  
  // Propiedades para detección de calidad de cámara
  private cameraCapabilities: Map<string, MediaTrackCapabilities> = new Map();
  private bestBackCamera: MediaDeviceInfo | null = null;
  private bestFrontCamera: MediaDeviceInfo | null = null;

  // Nueva propiedad para mostrar opciones
  public showCameraOptions = false;

  // Propiedades para manejar scroll vs clic
  private touchStartY = 0;
  private touchStartTime = 0;
  public isScrolling = false;
  private scrollThreshold = 10; // píxeles mínimos para considerar scroll
  private clickThreshold = 200; // milisegundos máximos para considerar clic

  private async checkMultipleWebcams() {
    try {
      // Verificar que tenemos permisos antes de enumerar
      try {
        const testStream = await navigator.mediaDevices.getUserMedia({ video: true });
        testStream.getTracks().forEach(track => track.stop());
      } catch (error) {
        this.multipleWebcamsAvailable = false;
        return;
      }

      // Enumerar dispositivos
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');

      // Limpiar arrays
      this.frontCameras = [];
      this.backCameras = [];
      this.cameraCapabilities.clear();

      // Agrupar por nombre del dispositivo y detectar capacidades
      for (const device of videoDevices) {
        const deviceLabel = device.label.toLowerCase();

        // Detectar cámara frontal por palabras clave
        if (deviceLabel.includes('front') ||
            deviceLabel.includes('selfie') ||
            deviceLabel.includes('user') ||
            deviceLabel.includes('face') ||
            deviceLabel.includes('frontal')) {
          this.frontCameras.push(device);
        } else {
          // Por defecto, asumir que es trasera
          this.backCameras.push(device);
        }

        // Obtener capacidades de cada cámara para determinar la mejor
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: { deviceId: { exact: device.deviceId } } 
          });
          const track = stream.getVideoTracks()[0];
          const capabilities = track.getCapabilities();
          this.cameraCapabilities.set(device.deviceId, capabilities);
          
          // Detener el stream inmediatamente
          stream.getTracks().forEach(track => track.stop());
        } catch (error) {
          console.warn(`No se pudieron obtener capacidades para ${device.label}:`, error);
        }
      }

      // Detectar la mejor cámara de cada grupo
      this.bestBackCamera = this.findBestCamera(this.backCameras);
      this.bestFrontCamera = this.findBestCamera(this.frontCameras);

      // Verificar si tenemos múltiples grupos
      const hasMultipleGroups = this.frontCameras.length > 0 && this.backCameras.length > 0;

      this.multipleWebcamsAvailable = hasMultipleGroups;

      if (this.multipleWebcamsAvailable) {
        // Inicializar con la mejor cámara trasera por defecto
        this.currentCameraGroup = 'back';
        this.currentGroupIndex = 0;
        this.availableDevices = this.backCameras;

        // Forzar detección de cambios en Angular
        this.changeDetectorRef.detectChanges();
      }
    } catch (error) {
      this.multipleWebcamsAvailable = false;
    }
  }

  // Método para encontrar la mejor cámara de un grupo
  private findBestCamera(cameras: MediaDeviceInfo[]): MediaDeviceInfo | null {
    if (cameras.length === 0) return null;
    if (cameras.length === 1) return cameras[0];

    let bestCamera = cameras[0];
    let bestScore = this.calculateCameraScore(cameras[0]);

    for (let i = 1; i < cameras.length; i++) {
      const score = this.calculateCameraScore(cameras[i]);
      if (score > bestScore) {
        bestScore = score;
        bestCamera = cameras[i];
      }
    }

    return bestCamera;
  }

  // Método para calcular un score de calidad de cámara
  private calculateCameraScore(device: MediaDeviceInfo): number {
    const capabilities = this.cameraCapabilities.get(device.deviceId);
    if (!capabilities) return 0;

    let score = 0;
    const label = device.label.toLowerCase();

    // Priorizar cámara principal (no ultra-wide, no telephoto, no macro)
    if (!label.includes('ultra') && !label.includes('wide') && 
        !label.includes('tele') && !label.includes('macro') &&
        !label.includes('0.5x') && !label.includes('2x') && !label.includes('3x')) {
      score += 1000; // Bonus por ser cámara principal
    }

    // Calcular score basado en resolución máxima
    if (capabilities.width && capabilities.width.max) {
      score += capabilities.width.max;
    }
    if (capabilities.height && capabilities.height.max) {
      score += capabilities.height.max;
    }

    // Bonus por frame rate alto
    if (capabilities.frameRate && capabilities.frameRate.max) {
      score += capabilities.frameRate.max * 10;
    }

    // Penalizar cámaras ultra-wide o telephoto para uso general
    if (label.includes('ultra') || label.includes('wide') || label.includes('0.5x')) {
      score -= 500;
    }
    if (label.includes('tele') || label.includes('2x') || label.includes('3x')) {
      score -= 300;
    }
    if (label.includes('macro')) {
      score -= 200;
    }

    return score;
  }

  // Método para obtener todas las cámaras disponibles con información
  public getAllCameraOptions(): Array<{device: MediaDeviceInfo, group: string, quality: string, isSelected: boolean}> {
    const options: Array<{device: MediaDeviceInfo, group: string, quality: string, isSelected: boolean}> = [];

    // Usar las mejores cámaras detectadas
    if (this.bestFrontCamera) {
      options.push({
        device: this.bestFrontCamera,
        group: 'Frontal',
        quality: this.getCameraQuality(this.bestFrontCamera),
        isSelected: this.currentCameraGroup === 'front' && this.currentGroupIndex === 0
      });
    }

    if (this.bestBackCamera) {
      options.push({
        device: this.bestBackCamera,
        group: 'Trasera',
        quality: this.getCameraQuality(this.bestBackCamera),
        isSelected: this.currentCameraGroup === 'back' && this.currentGroupIndex === 0
      });
    }

    return options;
  }

  // Método para determinar la calidad de la cámara
  private getCameraQuality(device: MediaDeviceInfo): string {
    const label = device.label.toLowerCase();

    if (label.includes('ultra') || label.includes('wide') || label.includes('0.5x') || label.includes('0.5')) {
      return 'Ultra-wide';
    } else if (label.includes('tele') || label.includes('2x') || label.includes('3x')) {
      return 'Telephoto';
    } else if (label.includes('macro')) {
      return 'Macro';
    } else {
      return 'Principal';
    }
  }

  // Método para seleccionar una cámara específica
  public selectSpecificCamera(device: MediaDeviceInfo): void {
    // Determinar a qué grupo pertenece
    const isFrontal = this.frontCameras.some(d => d.deviceId === device.deviceId);
    const isBack = this.backCameras.some(d => d.deviceId === device.deviceId);

    if (isFrontal) {
      this.currentCameraGroup = 'front';
      this.currentGroupIndex = this.frontCameras.findIndex(d => d.deviceId === device.deviceId);
      this.availableDevices = this.frontCameras;
    } else if (isBack) {
      this.currentCameraGroup = 'back';
      this.currentGroupIndex = this.backCameras.findIndex(d => d.deviceId === device.deviceId);
      this.availableDevices = this.backCameras;
    } else {
      console.error('Dispositivo no encontrado en ningún grupo');
      return;
    }

    // Detener cámara actual
    this.stopCamera();

    // Limpiar estado
    this.isInitialized = false;
    this.showWebcam = false;

    // Reiniciar cámara inmediatamente
    Promise.resolve().then(() => {
      this.startCamera();
    });
  }

  // Método para alternar la visualización de opciones
  public toggleCameraOptions(): void {
    this.showCameraOptions = !this.showCameraOptions;
  }

  // Método para manejar clics en opciones de cámara
  public onCameraOptionClick(device: MediaDeviceInfo, event: Event): void {
    // Si estábamos haciendo scroll, no procesar el clic
    if (this.isScrolling) {
      this.isScrolling = false;
      return;
    }

    // Prevenir comportamiento por defecto
    event.preventDefault();
    event.stopPropagation();

    // Llamar al método de selección
    this.selectSpecificCamera(device);
  }

  // Método para manejar inicio de touch
  public onTouchStart(event: TouchEvent): void {
    this.touchStartY = event.touches[0].clientY;
    this.touchStartTime = Date.now();
    this.isScrolling = false;
  }

  // Método para manejar movimiento de touch
  public onTouchMove(event: TouchEvent): void {
    const currentY = event.touches[0].clientY;
    const deltaY = Math.abs(currentY - this.touchStartY);

    if (deltaY > this.scrollThreshold) {
      this.isScrolling = true;
    }
  }

  // Método para manejar fin de touch
  public onTouchEnd(event: TouchEvent): void {
    const touchDuration = Date.now() - this.touchStartTime;

    // Si fue un toque muy corto y no hubo scroll, es un clic
    if (touchDuration < this.clickThreshold && !this.isScrolling) {
      // El clic se procesará en onCameraOptionClick
    } else {
      // Touch ignorado - scroll o muy largo
    }

    // Resetear estado
    this.isScrolling = false;
    this.touchStartY = 0;
    this.touchStartTime = 0;
  }

  // Método para trackBy en ngFor
  public trackByDeviceId(index: number, option: {device: MediaDeviceInfo, group: string, quality: string, isSelected: boolean}): string {
    return option.device.deviceId;
  }

  public async startCamera(): Promise<void> {
    try {
      // Esperar a que los elementos estén disponibles
      if (!this.videoElement || !this.canvasElement) {
        // Reintentar usando Promise.resolve().then() en lugar de setTimeout
        Promise.resolve().then(() => this.startCamera());
        return;
      }

      this.errorMessage = '';
      this.showWebcam = true;

      // Obtener la mejor cámara del grupo actual
      const bestCamera = this.currentCameraGroup === 'front' ? this.bestFrontCamera : this.bestBackCamera;
      const currentGroup = this.currentCameraGroup === 'front' ? this.frontCameras : this.backCameras;

      // Configurar opciones de video optimizadas para máxima calidad
      const videoOptions: MediaTrackConstraints = {
        width: { ideal: this.width, max: 4096 }, // Permitir resolución más alta
        height: { ideal: this.height, max: 4096 },
        frameRate: { ideal: 30, max: 60 }, // Permitir frame rate más alto
        aspectRatio: { ideal: 4/3 } // Mantener aspect ratio estándar
      };

      // Estrategia inteligente: usar la mejor cámara disponible
      if (bestCamera && bestCamera.deviceId) {
        // Usar la mejor cámara específica detectada
        videoOptions.deviceId = { exact: bestCamera.deviceId };
        
        // Obtener capacidades de la cámara para optimizar configuración
        const capabilities = this.cameraCapabilities.get(bestCamera.deviceId);
        if (capabilities) {
          // Usar la resolución máxima disponible si es mayor que la configurada
          if (capabilities.width && capabilities.width.max && capabilities.width.max > this.width) {
            videoOptions.width = { ideal: capabilities.width.max, max: capabilities.width.max };
          }
          if (capabilities.height && capabilities.height.max && capabilities.height.max > this.height) {
            videoOptions.height = { ideal: capabilities.height.max, max: capabilities.height.max };
          }
        }
      } else if (currentGroup.length > 0) {
        // Fallback: usar facingMode para aprovechar la fusión de cámaras (como apps nativas)
        videoOptions.facingMode = this.currentCameraGroup === 'front' ? 'user' : 'environment';
      } else {
        // Último fallback: usar facingMode genérico
        videoOptions.facingMode = this.currentCameraGroup === 'front' ? 'user' : 'environment';
      }

      // Obtener stream de la cámara
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: videoOptions,
        audio: false
      });

      // Configurar elemento de video
      if (this.videoElement && this.videoElement.nativeElement) {
        this.videoElement.nativeElement.srcObject = this.stream;

        // Esperar a que el video esté listo antes de reproducir
        await new Promise<void>((resolve) => {
          this.videoElement.nativeElement.onloadedmetadata = () => {
            resolve();
          };
        });

        await this.videoElement.nativeElement.play();
      }

      // Configurar canvas
      if (this.canvasElement && this.canvasElement.nativeElement) {
        const canvas = this.canvasElement.nativeElement;
        canvas.width = this.width;
        canvas.height = this.height;
      }

      this.isInitialized = true;
    } catch (error: any) {
      this.handleInitError(error);
    }
  }

  public stopCamera(): void {
    if (this.stream) {
      this.stream.getTracks().forEach(track => {
        track.stop();
      });
      this.stream = null;
    }

    if (this.videoElement && this.videoElement.nativeElement) {
      this.videoElement.nativeElement.srcObject = null;
    }

    this.isInitialized = false;
    this.showWebcam = false;
  }

  public toggleWebcam(): void {
    if (this.showWebcam) {
      this.stopCamera();
    } else {
      this.startCamera();
    }
  }

  public handleInitError(error: any): void {
    let errorMessage = 'Error al inicializar la cámara';

    if (error.message) {
      if (error.message.includes('Permission denied')) {
        errorMessage = 'Permiso denegado para acceder a la cámara';
      } else if (error.message.includes('NotAllowedError')) {
        errorMessage = 'Acceso a la cámara denegado';
      } else if (error.message.includes('NotFoundError')) {
        errorMessage = 'Cámara no encontrada';
      } else if (error.message.includes('NotReadableError')) {
        errorMessage = 'Cámara en uso por otro aplicativo';
      } else if (error.message.includes('OverconstrainedError')) {
        errorMessage = 'Cámara no cumple con los requisitos';
      } else {
        errorMessage = `Error: ${error.message}`;
      }
    }

    this.errorMessage = errorMessage;
    this.cameraError.emit(this.errorMessage);
    this.showWebcam = false;
  }

  public triggerSnapshot(): void {
    if (!this.isInitialized) {
      this.startCamera().then(() => {
        // Reintentar la captura después de inicializar usando Promise.resolve().then()
        Promise.resolve().then(() => this.triggerSnapshot());
      });
      return;
    }

    if (!this.videoElement || !this.canvasElement) {
      this.errorMessage = 'Elementos de cámara no disponibles';
      this.cameraError.emit(this.errorMessage);
      return;
    }

    try {
      const video = this.videoElement.nativeElement;
      const canvas = this.canvasElement.nativeElement;
      const context = canvas.getContext('2d');

      if (!context) {
        this.errorMessage = 'No se pudo obtener el contexto del canvas';
        this.cameraError.emit(this.errorMessage);
        return;
      }

      // Obtener dimensiones reales del video
      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;

      // Configurar canvas con las dimensiones reales del video para máxima calidad
      canvas.width = videoWidth;
      canvas.height = videoHeight;

      // Aplicar configuración de alta calidad al contexto
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';

      // Dibujar el frame actual del video en el canvas sin escalar
      context.drawImage(video, 0, 0, videoWidth, videoHeight, 0, 0, videoWidth, videoHeight);

      // IMAGEN ORIGINAL: Calidad máxima para enviar al remoto (sin compresión)
      const originalImageData = canvas.toDataURL('image/jpeg', 1.0);

      // IMAGEN PARA PREVIEW: Calidad optimizada para UI (más pequeña)
      const previewImageData = canvas.toDataURL('image/jpeg', this.quality);

      // Crear objeto de imagen con la ORIGINAL de alta calidad
      const imageData: WebcamImageData = {
        fileName: `webcam_${new Date().getTime()}.jpg`,
        size: this.getBase64Size(originalImageData), // Tamaño de la imagen original
        mimeType: 'image/jpeg',
        base64Data: originalImageData, // IMAGEN ORIGINAL para enviar al remoto
        previewData: previewImageData, // IMAGEN OPTIMIZADA para preview
        file: originalImageData.split(',')[1], // Solo la parte base64 de la original
        timestamp: new Date()
      };

      this.value = imageData;
      this.onChange(this.value);
      this.photoCaptured.emit(this.value);
      this.errorMessage = '';
    } catch (error: any) {
      this.errorMessage = 'Error al capturar la imagen';
      this.cameraError.emit(this.errorMessage);
    }
  }

  public cameraWasSwitched(deviceId: string): void {
    if (!this.multipleWebcamsAvailable) {
      return;
    }

    // Cambiar entre grupos (frontal ↔ trasera)
    if (this.currentCameraGroup === 'back') {
      this.currentCameraGroup = 'front';
      this.currentGroupIndex = 0;
      this.availableDevices = this.frontCameras;
    } else {
      this.currentCameraGroup = 'back';
      this.currentGroupIndex = 0;
      this.availableDevices = this.backCameras;
    }

    // Detener cámara actual
    this.stopCamera();

    // Limpiar estado completamente
    this.isInitialized = false;
    this.showWebcam = false;

    // Reiniciar cámara inmediatamente usando Promise.resolve().then()
    Promise.resolve().then(() => {
      this.startCamera();
    });
  }

  // Método para alternar entre cámara frontal y trasera
  public toggleCamera(): void {
    if (!this.multipleWebcamsAvailable) {
      return;
    }

    // Cambiar entre grupos (frontal ↔ trasera)
    if (this.currentCameraGroup === 'back') {
      this.currentCameraGroup = 'front';
      this.currentGroupIndex = 0;
      this.availableDevices = this.frontCameras;
    } else {
      this.currentCameraGroup = 'back';
      this.currentGroupIndex = 0;
      this.availableDevices = this.backCameras;
    }

    // Detener cámara actual
    this.stopCamera();

    // Limpiar estado
    this.isInitialized = false;
    this.showWebcam = false;

    // Reiniciar cámara inmediatamente con la mejor cámara del grupo
    Promise.resolve().then(() => {
      this.startCamera();
    });
  }

  // Método para obtener el icono actual de la cámara
  public getCurrentCameraIcon(): string {
    return this.currentCameraGroup === 'front' ? '📱' : '📷';
  }

  // Método para obtener el tooltip actual
  public getCurrentCameraTooltip(): string {
    const groupName = this.currentCameraGroup === 'front' ? 'Frontal' : 'Trasera';
    return `Cambiar a cámara ${this.currentCameraGroup === 'front' ? 'trasera' : 'frontal'}`;
  }

  retakePhoto() {
    this.value = null;
    this.onChange(this.value);
    this.errorMessage = '';
    this.showWebcam = true;
    this.startCamera();
    this.photoDeleted.emit();
  }

  deletePhoto() {
    this.value = null;
    this.onChange(this.value);
    this.errorMessage = '';
    this.photoDeleted.emit();
  }

  // Full size image methods
  openFullSizeImage(): void {
    this.showFullSizeModal = true;
  }

  closeFullSizeImage(): void {
    this.showFullSizeModal = false;
  }

  private getBase64Size(base64String: string): number {
    const stringLength = base64String.length;
    const sizeInBytes = 4 * Math.ceil(stringLength / 3) * 0.75;
    return Math.round(sizeInBytes);
  }

  formatBytes(bytes: number, decimals: number = 2) {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals || 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  getImageDimensions(): string {
    if (this.value && this.value.base64Data) {
      // Crear una imagen temporal para obtener dimensiones reales
      const img = new Image();
      img.onload = () => {
      };
      img.src = this.value.base64Data;

      // Retornar dimensiones reales si están disponibles
      if (this.value.base64Data.includes('data:image/')) {
        return `${this.value.base64Data.split(',')[1] ? 'Dimensiones reales' : 'Dimensiones configuradas'}`;
      }
    }

    // Fallback a dimensiones configuradas
    return `${this.width} x ${this.height}`;
  }

  // ControlValueAccessor implementation
  writeValue(value: WebcamImageData | null): void {
    this.value = value;
    this.valueChange.next(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.stopCamera();
    }
  }
}
